import React, { useState, useEffect, useRef } from 'react';
import { ChatWindow } from './ChatWindow';
import { ChatTrigger } from './ChatTrigger';
import { useAIChat } from '@/hooks/useAIChat';

interface ChatWidgetProps {
  className?: string;
}

export const ChatWidget: React.FC<ChatWidgetProps> = ({ className = '' }) => {
  const {
    state,
    actions: {
      openChat,
      closeChat,
      minimizeChat,
      maximizeChat,
      sendMessage,
      clearError,
      markAsRead
    }
  } = useAIChat();

  const [isDragging, setIsDragging] = useState(false);
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const widgetRef = useRef<HTMLDivElement>(null);

  // 处理拖拽功能（仅在最小化状态下）
  const handleMouseDown = (e: React.MouseEvent) => {
    if (!state.isMinimized) return;

    setIsDragging(true);
    const rect = widgetRef.current?.getBoundingClientRect();
    if (rect) {
      setPosition({
        x: e.clientX - rect.left,
        y: e.clientY - rect.top
      });
    }
  };

  const handleMouseMove = (e: MouseEvent) => {
    if (!isDragging || !state.isMinimized) return;

    const newX = e.clientX - position.x;
    const newY = e.clientY - position.y;

    if (widgetRef.current) {
      widgetRef.current.style.left = `${Math.max(0, Math.min(window.innerWidth - 384, newX))}px`;
      widgetRef.current.style.top = `${Math.max(0, Math.min(window.innerHeight - 512, newY))}px`;
    }
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  useEffect(() => {
    if (isDragging) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      return () => {
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
      };
    }
  }, [isDragging, position]);

  // 自动标记为已读（当窗口打开时）
  useEffect(() => {
    if (state.isOpen && !state.isMinimized && state.unreadCount > 0) {
      markAsRead();
    }
  }, [state.isOpen, state.isMinimized, state.unreadCount, markAsRead]);

  if (!state.isOpen) {
    return (
      <ChatTrigger
        onClick={openChat}
        unreadCount={state.unreadCount}
        className={className}
      />
    );
  }

  return (
    <div
      ref={widgetRef}
      className={`fixed z-50 ${className}`}
      style={{
        right: state.isMinimized ? 'auto' : '20px',
        bottom: state.isMinimized ? 'auto' : '20px',
        left: state.isMinimized ? '20px' : 'auto',
        top: state.isMinimized ? 'auto' : 'auto',
        cursor: isDragging ? 'grabbing' : state.isMinimized ? 'grab' : 'default'
      }}
    >
      {state.isMinimized ? (
        <div
          className="flex items-center bg-blue-600 text-white px-4 py-2 rounded-full shadow-lg hover:bg-blue-700 transition-colors cursor-pointer select-none"
          onMouseDown={handleMouseDown}
          onClick={maximizeChat}
        >
          <div className="w-2 h-2 bg-green-400 rounded-full mr-2 animate-pulse" />
          <span className="text-sm font-medium">智能客服"大白"</span>
          {state.unreadCount > 0 && (
            <div className="ml-2 bg-red-500 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center">
              {state.unreadCount > 9 ? '9+' : state.unreadCount}
            </div>
          )}
        </div>
      ) : (
        <ChatWindow
          state={state}
          onClose={closeChat}
          onMinimize={minimizeChat}
          onSendMessage={sendMessage}
          onClearError={clearError}
        />
      )}
    </div>
  );
};